"use client";
import React from "react";
import styles from "./MagazineCard.module.scss";
import { customSanitize } from "@/utils/customSanitize";
import { ImageType } from "@/types/baseTypes";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { MediaItem } from "../MediaItem/MediaItem";
import { Button } from "../Button/Button";

export type MagazineCardElement = {
  title: string;
  subline: string;
  excerpt: string;
  image: ImageType | false;
  link: string;
};

interface MagazineCardProps {
  element: MagazineCardElement;
  show_border_radius: boolean;
  show_box_shadow: boolean;
  showBox?: boolean;
  fallbackImage: ImageType | false | undefined;
  heading_level: number;
  skeleton?: boolean;
}

export const MagazineCard = ({
  element,
  showBox = false,
  show_box_shadow,
  show_border_radius,
  fallbackImage,
  heading_level,
  skeleton = false,
}: MagazineCardProps) => {
  const skeletonElementClass = skeleton ? "skeleton-element" : "";
  const { getLabel } = useGlobalContext();

  return (
    <article
      className={`magazine-card ${styles["magazine-card"]} m-0  columns is-multiline  ${showBox ? "is-box" : ""} ${show_border_radius && showBox ? "has-border-radius" : ""} ${show_box_shadow && showBox ? "has-box-shadow" : ""}`}
    >
      <div className={`column is-6-tablet p-0`}>
        <MediaItem
          image_desktop={element.image || fallbackImage || false}
          sizes="(max-width: 1023px) 100vw, 50vw"
          objectFit="cover"
          className={`${skeletonElementClass}`}
        />
      </div>
      <div className={`column is-6-tablet p-5`}>
        <header className={`${element.subline ? "has-subline" : ""}`}>
          <p
            role="heading"
            aria-level={heading_level}
            className={`card-title is-block ${skeletonElementClass}`}
            dangerouslySetInnerHTML={{ __html: customSanitize(element.title) }}
          />
          {element.subline && (
            <p
              className={`card-subline ${skeletonElementClass}`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(element.subline),
              }}
            />
          )}

          <p
            className={`mt-4 ${skeletonElementClass}`}
            dangerouslySetInnerHTML={{
              __html: customSanitize(element.excerpt),
            }}
          />
        </header>

        {element.link && (
          <footer>
            <Button
              type="secondary"
              aria-label={getLabel("aria_label_postlink").replace(
                `"$posttitle"`,
                element.title,
              )}
              href={element.link}
              target="_self"
              className={`${skeletonElementClass}`}
            >
              {getLabel("read_more_label")}
            </Button>
          </footer>
        )}
      </div>
    </article>
  );
};
